/* eslint-disable import/extensions */
import { Container, Button, Row, Col, NavDropdown } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import Seo from '@/components/seo';
import { useRouter } from 'next/router';
import { controller, format, generator, http, layout, messages, notification } from '@/helpers/index';
import {
  FaEye,
  FaHeart,
  FaLocationDot,
  FaCalendarDays,
  FaGears,
  FaGasPump,
  FaGaugeHigh,
  FaShieldHalved,
  FaArrowRight
} from 'react-icons/fa6';
import { auth } from '@/services/auth';
import FilterAndPagination from '@/components/actions/filter-and-pagination';

function Classifieds() {
  const router = useRouter();
  const user = auth.store.logged.get();
  const [favorites, setFavorites] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const labels = {
    gearbox: {
      MANUAL: 'Manual',
      AUTOMATED: 'Automatizado',
      AUTOMATIC: 'Automático'
    }
  };

  const manager = controller.configure({
    cachePrefix: 'seminovos',
    prefix: '/',
    name: 'classifieds/general',
    setItems,
    setPage,
    setPages,
    setTotal,
  });

  useEffect(() => {
    manager.load();
    if (!user) { return; }
    http.get('/favorites', (result) => {
      setFavorites(result);
    });
  }, []);

  const handleFavorite = (item) => {
    if (!user) {
      notification.confirm('<strong>Para favoritar um anúncio é necessário estar logado.</strong><br>Deseja ser redirecionado para efetuar login?', () => {
        layout.back.set('/seminovos');
        router.push('/entrar');
      });
      return;
    }
    messages.clear();
    const index = favorites.findIndex((fav) => fav.classifiedId === item._id);
    if (index !== -1) {
      const favorite = favorites.find((fav) => fav.classifiedId === item._id);
      http.delete(`/favorites/${favorite._id}`, null, (result) => {
        if (result) {
          favorites.splice(index, 1);
          setFavorites([...favorites]);
          return;
        }
        messages.error('Não foi possível remover este anúncio dos seus favoritos. Por favor, tente novamente mais tarde.', false, true);
      });
      return;
    }
    http.post('/favorites', {
      type: 'CLASSIFIED',
      classifiedId: item._id
    }, (result) => {
      if (result) {
        favorites.push(result);
        setFavorites([...favorites]);
        return;
      }
      messages.error('Não foi possível favoritar este anúncio. Por favor, tente novamente mais tarde.', false, true);
    });
  };

  const getLink = (item) => {
    return `${process.env.WEB_URI}/seminovos/${item._id}`;
  };

  const getYear = (data) => {
    if (data.manufactureYear && data.modelYear) {
      return `${data.manufactureYear}/${data.modelYear}`;
    }
    if (data.manufactureYear) {
      return data.manufactureYear;
    }
    if (data.modelYear) {
      return data.modelYear;
    }
  };

  const getLocation = (data) => {
    if (data.location) {
      if (data.location.includes('/')) {
        return data.location;
      }
      const parts = data.location.split(', ');
      if (parts.length > 2) {
        parts.pop();
      }
      return parts.join('/');
    }
    if (data.city && data.state) {
      return `${data.city}/${data.state}`;
    }
    return 'Brasil';
  };

  const getSpecPills = (item) => {
    const { data } = item;
    const pills = [];
    const year = getYear(data);
    if (year) {
      pills.push({ icon: <FaCalendarDays />, text: year });
    }
    if (data.km) {
      pills.push({ icon: <FaGaugeHigh />, text: `${format.number ? format.number(data.km) : data.km} km` });
    }
    if (data.gearbox) {
      pills.push({ icon: <FaGears />, text: labels.gearbox[data.gearbox] ?? data.gearbox });
    }
    if (data.fuel) {
      pills.push({ icon: <FaGasPump />, text: data.fuel });
    }
    return pills;
  };

  const sort = (value) => {
    manager.filterIt('sort', value);
  };

  const getCurrentSort = () => {
    const sorted = {
      RELEVANCE: 'Relevância',
      MINOR: 'Menor Preço',
      MAJOR: 'Maior Preço',
    };
    return sorted[manager.filter.sort ?? 'RELEVANCE'];
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://aboutcar.com.br/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Seminovos',
        item: 'https://aboutcar.com.br/seminovos'
      }
    ]
  };

  return (
    <>
      <Seo
        title="Carros Seminovos e Usados à Venda | AboutCar"
        description="Encontre carros seminovos e usados à venda com histórico veicular completo e verificado pela AboutCar. Compre seu próximo veículo com total segurança!"
        keywords="carros seminovos, carros usados, comprar carro com historico, seminovos verificados, ofertas de carros"
        schema={breadcrumbSchema}
      />
      <Container className="classifieds py-3">
        <div className="seminovos-hero text-center not-printable my-3">
          <div className="hero-badge mb-3">
            <FaShieldHalved /> Histórico Completo & Verificado
          </div>
          <h1 className="mb-2">Seminovos e Usados de Confiança</h1>
          <p className="text-muted">
            Encontre o veículo ideal com relatório de histórico transparente.
          </p>
        </div>

        <FilterAndPagination
          page={page}
          pages={pages}
          total={total}
          manager={manager}
          ignoreMirror
          placeholder="Qual veículo você está procurando? (Ex: Civic, Corolla, SUV...)"
          config={{
            record: 'veículo',
            records: 'veículos',
            empty: 'Nenhum veículo encontrado.',
            size: 'lg',
            limits: [12, 24, 36, 48, 60, 72, 84, 96],
            accordion: 'filters',
            ignoreRecords: true
          }}
          hideSaved
        >
          <div className="classifieds-sort-container mb-3">
            <span className="fw-semibold text-muted small">
              {total ? `Exibindo ${total} veículo${total > 1 ? 's' : ''}` : 'Buscando veículos...'}
            </span>
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted d-none d-sm-inline">Ordenar por:</span>
              <NavDropdown title={getCurrentSort()} id="sort" className="fw-bold border rounded px-2 py-1 bg-white">
                <NavDropdown.Item href="" onClick={() => sort('RELEVANCE')} active={!manager.filter.sort || manager.filter.sort === 'RELEVANCE'}>Relevância</NavDropdown.Item>
                <NavDropdown.Item href="" onClick={() => sort('MINOR')} active={manager.filter.sort && manager.filter.sort === 'MINOR'}>Menor Preço</NavDropdown.Item>
                <NavDropdown.Item href="" onClick={() => sort('MAJOR')} active={manager.filter.sort && manager.filter.sort === 'MAJOR'}>Maior Preço</NavDropdown.Item>
              </NavDropdown>
            </div>
          </div>

          <Row className="g-4 mb-4">
            {items.map((el) => {
              const isFav = favorites?.some((fav) => fav.classifiedId === el._id);
              const pills = getSpecPills(el);
              const link = getLink(el);

              return (
                <Col key={generator.token()} xs={12} sm={6} lg={4} xl={3} className="d-flex">
                  <div className="classifieds-card w-100">
                    <div className="classifieds-img-wrapper" onClick={() => window.open(link)}>
                      {/* <span className="classifieds-verified-badge">
                        <FaShieldHalved /> Verificado
                      </span> */}
                      <Button
                        variant="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFavorite(el);
                        }}
                        title={isFav ? 'Desfavoritar' : 'Favoritar'}
                        className="classifieds-fav-btn"
                      >
                        <FaHeart className={isFav ? 'text-danger' : 'text-muted'} />
                      </Button>
                      <img src={el.images[0]?.url} alt={el.data.description || `${el.data.brand} ${el.data.model}`} loading="lazy" />
                    </div>

                    <div className="classifieds-card-body" onClick={() => window.open(link)}>
                      <h3 className="classifieds-card-title">
                        {el.data.description || `${el.data.brand} ${el.data.model}`}
                      </h3>
                      <div className="classifieds-card-subtitle">
                        {el.data.version || `${el.data.brand} ${el.data.model}`}
                      </div>

                      <div className="classifieds-price-tag">
                        {format.currency(el.data.value)}
                      </div>

                      {pills.length > 0 && (
                        <div className="classifieds-specs-grid">
                          {pills.map((pill) => (
                            <span key={generator.token()} className="classifieds-spec-pill">
                              {pill.icon}
                              <span>{pill.text}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="p-3 pt-0">
                      <Button variant="primary" className="classifieds-action-btn d-flex align-items-center justify-content-center gap-2" onClick={() => window.open(link)}>
                        <span>Ver Detalhes</span>
                        <FaArrowRight size={12} />
                      </Button>
                    </div>

                    <div className="classifieds-card-footer" onClick={() => window.open(link)}>
                      <span className="classifieds-location">
                        <FaLocationDot className="text-danger flex-shrink-0" />
                        <span>{getLocation(el.data)}</span>
                      </span>
                      <span className="classifieds-views-badge">
                        <FaEye /> {el.views}
                      </span>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </FilterAndPagination>
      </Container>
    </>
  );
}

Classifieds.layout = 'classifieds';
Classifieds.enableMessages = true;
export default Classifieds;
